import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainContainer from "../../../layout/MainContainer";
import { Button } from "@mui/material";
import AddNewRole from "../../../Modals/addNewRole";

import { CommonBody, DataType, RoleTypes } from "../../../types/General";
import { errorToast, successToast } from "../../../helpers";
import {
  useAddRoleMutation,
  useLazyDeleteRoleQuery,
  useLazyGetAllRolesQuery,
  useLazyRoleDetailQuery,
  useUpdateRoleMutation,
} from "../../../service/subadmin";
import { generateEncryptedKeyBody } from "../../../utils/crypto";
import Loader from "../../../constants/Loader";
import { ConfirmationModal } from "../../../Modals/confirmationModal";

let flag: boolean;
const defaultPermissions: DataType[] = [
  {
    module_id: 1,
    label: "Dashboard",
    can_add_edit: false,
    can_view: false,
    can_be_delete: false,
  },
  {
    module_id: 2,
    label: "Users",
    can_add_edit: false,
    can_view: false,
    can_be_delete: false,
  },
  {
    module_id: 3,
    label: "Visa Type",
    can_add_edit: false,
    can_view: false,
    can_be_delete: false,
  },
  {
    module_id: 4,
    label: "Manage Applications",
    can_add_edit: false,
    can_view: false,
    can_be_delete: false,
  },
  {
    module_id: 5,
    label: "Manage Countries",
    can_add_edit: false,
    can_view: false,
    can_be_delete: false,
  },
  {
    module_id: 6,
    label: "Manage Citizenship",
    can_add_edit: false,
    can_view: false,
    can_be_delete: false,
  },
  {
    module_id: 7,
    label: "Manage Documents",
    can_add_edit: false,
    can_view: false,
    can_be_delete: false,
  },
  {
    module_id: 10,
    label: "Manage Salaries",
    can_add_edit: false,
    can_view: false,
    can_be_delete: false,
  },
  {
    module_id: 11,
    label: "Manage Payments",
    can_add_edit: false,
    can_view: false,
    can_be_delete: false,
  },
  {
    module_id: 12,
    label: "Reports and Analytics",
    can_add_edit: false,
    can_view: false,
    can_be_delete: false,
  },
  {
    module_id: 13,
    label: "Customer Support",
    can_add_edit: false,
    can_view: false,
    can_be_delete: false,
  },
  {
    module_id: 14,
    label: "Manage Notifications",
    can_add_edit: false,
    can_view: false,
    can_be_delete: false,
  },
  {
    module_id: 15,
    label: "Manage CMS",
    can_add_edit: false,
    can_view: false,
    can_be_delete: false,
  },
];

const Roles = () => {
  const navigate = useNavigate();
  const [roleName, setRoleName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [roles, setRoles] = useState<RoleTypes[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);

  const [openModal, setOpenModal] = useState(false);
  const handleCloseModal = () => {
    setOpenModal(false);
    setRoleName("");
    setSelectedRoleId(null);
    setPermissions(defaultPermissions);
  };

  const [openWarnModal, setOpenWarnModal] = useState(false);

  const [getRoles] = useLazyGetAllRolesQuery();
  const [addRole] = useAddRoleMutation();
  const [updateRole] = useUpdateRoleMutation();
  const [roleDetail] = useLazyRoleDetailQuery();
  const [deleteRole] = useLazyDeleteRoleQuery();

  const [permissions, setPermissions] =
    useState<DataType[]>(defaultPermissions);

  const handleSubmit = async () => {
    const isAnyPermissionSelected = permissions?.some(
      (permission) =>
        permission.can_view ||
        permission.can_add_edit ||
        permission.can_be_delete
    );
    if (roleName === "") return;

    if (!isAnyPermissionSelected) {
      errorToast("Please assign atleast one permission"); // Show error message if no permission is selected
      return;
    }
    const body = {
      role_name: roleName,
      permissions: permissions,
    };

    try {
      setLoading(true);
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      let response;
      if (selectedRoleId) {
        response = await updateRole({
          body: encryptedBody,
          id: selectedRoleId,
        }).unwrap();
      } else {
        response = await addRole(encryptedBody).unwrap();
      }

      if (response?.status === 200) {
        setLoading(false);

        successToast(
          `Role ${selectedRoleId ? "updated" : "added"} successfully`
        );

        setRoleName("");
        setOpenModal(false);
        fetchRoles();
      } else {
        setLoading(false);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await getRoles().unwrap();
      setLoading(false);

      if (response?.status === 200) {
        setRoles(response?.data || []);
      } else {
        setRoles([]);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const fetchRoleDetails = async (selectedRoleId: number) => {
    setLoading(true);
    try {
      const response = await roleDetail({ id: selectedRoleId }).unwrap();
      setLoading(false);

      if (response?.status === 200) {
        setRoleName(response?.data?.role_name || "-");
        const updatedPermissions = permissions.map((permission) => {
          const rolePermission = response?.data?.role_permission.find(
            (rolePerm) => rolePerm?.module_id === permission?.module_id
          );

          if (rolePermission) {
            return {
              ...permission,
              can_view: rolePermission?.can_view,
              can_add_edit: rolePermission?.can_add_edit,
              can_be_delete: rolePermission?.can_be_delete,
            };
          }
          return permission;
        });

        setPermissions(updatedPermissions);
      } else {
        setRoles([]);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const response = await deleteRole({ id: selectedRoleId }).unwrap();
      setLoading(false);

      if (response?.status === 200) {
        successToast("Role deleted successfully");
        fetchRoles();
      } else {
        setRoles([]);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    if (selectedRoleId && flag) {
      fetchRoleDetails(selectedRoleId);
    }
  }, [selectedRoleId, openModal]);

  return (
    <>
      <MainContainer>
        <Loader isLoad={loading} />
        <div className="main_title d_flex">
          <div className="left_s">
            <h1>Roles</h1>
            <p>
              <span onClick={() => navigate("/dashboard")}>Dashboard</span> -
              User Management - Roles
            </p>
          </div>
          <div className="right_s">
            <Button
              className="btn btn_secondary"
              onClick={() => setOpenModal(true)}
            >
              Create Roles
            </Button>
          </div>
        </div>

        <div className="roles_list gap_m">
          {roles?.length ? (
            roles?.map((item) => {
              // Filter the permissions where can_view is true
              const viewableModules = item?.role_permission
                ?.filter((permission) => permission?.can_view)
                ?.map((permission) => permission?.module);

              return (
                <div className="cards" key={item.id}>
                  <h2>{item?.role_name || ""}</h2>
                  <p>Total users with this role : {item?.assigned_to || 0}</p>
                  <ul>
                    {viewableModules?.length > 0 ? (
                      viewableModules?.map((module, index) => (
                        <li key={index}>{module || "-"}</li>
                      ))
                    ) : (
                      <li>No modules available</li>
                    )}
                  </ul>
                  <div className="btn_flex">
                    <Button
                      className="btn btn_highlight"
                      onClick={() => {
                        flag = true;
                        setSelectedRoleId(item?.id);
                        setOpenModal(true);
                      }}
                    >
                      Edit Role
                    </Button>
                    <Button
                      className="btn btn_highlight"
                      onClick={() => {
                        flag = false;
                        setSelectedRoleId(item?.id);
                        setOpenWarnModal(true);
                      }}
                    >
                      Delete Role
                    </Button>
                  </div>
                </div>
              );
            })
          ) : (
            <div
              style={{
                justifyContent: "center",
                display: "flex",
                paddingTop: "170px",
              }}
            >
              <p style={{ fontSize: "14px", color: "black" }}>
                {" "}
                No roles found
              </p>
            </div>
          )}
        </div>
      </MainContainer>

      <AddNewRole
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
        permissions={permissions}
        setPermissions={setPermissions}
        setRoleName={setRoleName}
        roleName={roleName}
        handleSubmit={handleSubmit}
      />
      <ConfirmationModal
        text={"Are you sure to delete this role?"}
        openModal={openWarnModal}
        setOpenModal={setOpenWarnModal}
        handleSubmit={handleDelete}
      />
    </>
  );
};

export default Roles;
